/**
 * The gong: one sound, heard by everyone in the call at once.
 *
 * Sessions have moments that are marked rather than spoken — the start of a
 * silence, the end of an exercise, a breath everyone takes together. Saying
 * "right, that's time" over a patient who is still inside the exercise is
 * exactly the interruption the moment is supposed not to have.
 *
 * What travels is the *event*, not the audio. Every browser plays its own
 * copy from a local oscillator, so the gong never goes near the microphone
 * track: mixing it into the therapist's audio would leave it fighting the
 * echo canceller, arriving late and thin, and being cut in half whenever they
 * were muted.
 */

/** LiveKit multiplexes one data channel; the topic keeps the gong apart. */
export const GONG_TOPIC = "alchely-gong";

/**
 * What one browser tells the others.
 *
 * A gong carries its whole plan rather than one packet per strike, so every
 * browser runs the same sequence off its own clock: the patient's countdown
 * keeps ticking through a dropped packet or a reconnection, and a therapist
 * whose laptop sleeps mid-session does not leave the room waiting for a gong
 * that will never be sent.
 */
export type GongMessage =
    | {
          kind: "gong";
          mode?: GongMode;
          spanMinutes?: number;
          openStrikes?: number;
          closeStrikes?: number;
      }
    /** Called off. A sequence nobody can stop is worse than no sequence. */
    | { kind: "gong-stop" };

/**
 * The two things a gong can be.
 *
 * `single` sounds where you press it and is done. `timed` opens, waits, and
 * closes — the shape of an exercise, a silence, a meditation: a therapist
 * sets it going and stops watching the clock, which is the whole point.
 *
 * There is deliberately nothing in between. A run of marks every few minutes
 * was offered here for a while and it answered a question nobody asked: what
 * a session needs is a beginning and an end, and every mark between them is
 * one more interruption of the thing the silence was for.
 */
export type GongMode = "single" | "timed";

export const GONG_MODES: readonly GongMode[] = ["single", "timed"];

export function isGongMode(value: unknown): value is GongMode {
    return value === "single" || value === "timed";
}

/**
 * How a gong is set up: which of the two it is, how long it lasts, and how
 * many sounds each end is marked with.
 */
export type GongPlan = {
    mode: GongMode;
    /** From the opening to the closing. Says nothing about a single gong. */
    spanMinutes: number;
    /**
     * How many sounds the opening is made of.
     *
     * A practice bowl is rarely struck once. Three at the start says "we are
     * beginning now" in a way one cannot: the first is heard, the second is
     * recognised, and by the third everyone in the room has arrived at the
     * same place. The same three at the end let a person come out of a long
     * silence gradually instead of being pulled out of it.
     */
    openStrikes: number;
    /** The same, for the closing. Says nothing about a single gong. */
    closeStrikes: number;
};

export const DEFAULT_GONG_PLAN: GongPlan = {
    mode: "single",
    spanMinutes: 10,
    openStrikes: 1,
    closeStrikes: 1,
};

/** Where the therapist's own setting is kept, per browser. */
export const GONG_PLAN_STORAGE_KEY = "alchely.meeting.gong";

/** The limits are a session's, not a format's: nothing outlasts the call. */
export const MIN_GONG_SPAN_MINUTES = 1;
export const MAX_GONG_SPAN_MINUTES = 180;

/**
 * Three is as many as an opening or a closing can be.
 *
 * Past three nobody is counting bowls any more, they are waiting for them to
 * stop — and a longer run of them stops reading as a marker and starts
 * reading as a fault.
 */
export const MAX_GONG_CLUSTER = 3;

/**
 * The gap between the sounds of one opening or closing.
 *
 * Long enough that each is a separate sound rather than a roll, short enough
 * that three of them are still one gesture. It has to clear
 * `GONG_MIN_INTERVAL_MS` as well, or the room's own guard against stacked
 * gongs would swallow the second and the third.
 */
export const GONG_CLUSTER_GAP_MS = 4000;

/** Spans offered in the control, in minutes: the usual shapes of a session. */
export const GONG_SPAN_CHOICES: readonly number[] = [1, 3, 5, 10, 15, 20, 30, 45, 60];

/** One, two or three: the whole of what an opening or a closing may be. */
export const GONG_CLUSTER_CHOICES: readonly number[] = [1, 2, 3];

function clamp(value: number, low: number, high: number): number {
    return Math.min(high, Math.max(low, value));
}

/**
 * A plan, from anywhere at all.
 *
 * Used on the plan a browser sends us and on the one this browser remembered
 * last time, because both are input: the packet comes from someone else's
 * machine, and `localStorage` is whatever was last written there. Twelve
 * hundred sounds across a millisecond is a denial of service made of sound,
 * so the bounds are applied here rather than trusted from the sender.
 *
 * A message with no plan in it is a single gong, now — which is what a bare
 * `{ kind: "gong" }` has always meant.
 */
export function normalizeGongPlan(raw: unknown): GongPlan | null {
    if (typeof raw !== "object" || raw === null) return null;

    const source = raw as {
        mode?: unknown;
        spanMinutes?: unknown;
        openStrikes?: unknown;
        closeStrikes?: unknown;
    };

    const spanMinutes =
        source.spanMinutes === undefined
            ? DEFAULT_GONG_PLAN.spanMinutes
            : Math.round(Number(source.spanMinutes));
    const openStrikes =
        source.openStrikes === undefined ? 1 : Math.round(Number(source.openStrikes));
    const closeStrikes =
        source.closeStrikes === undefined ? 1 : Math.round(Number(source.closeStrikes));

    if (
        !Number.isFinite(spanMinutes) ||
        !Number.isFinite(openStrikes) ||
        !Number.isFinite(closeStrikes)
    ) {
        return null;
    }

    return {
        mode: isGongMode(source.mode) ? source.mode : "single",
        spanMinutes: clamp(spanMinutes, MIN_GONG_SPAN_MINUTES, MAX_GONG_SPAN_MINUTES),
        openStrikes: clamp(openStrikes, 1, MAX_GONG_CLUSTER),
        closeStrikes: clamp(closeStrikes, 1, MAX_GONG_CLUSTER),
    };
}

/** A moment the gong marks, and how many sounds it is marked with. */
export type GongMarker = {
    /** Milliseconds from the press. */
    offset: number;
    sounds: number;
};

/**
 * The moments a plan marks, in milliseconds from the press.
 *
 * A single gong marks one moment, and its opening is the whole of it — there
 * is no end to a thing that has no middle, so `closeStrikes` says nothing
 * there. A timed one marks two, the span apart, and the silence between them
 * is what the therapist actually set.
 */
export function gongMarkers(plan: GongPlan): GongMarker[] {
    const opening = { offset: 0, sounds: plan.openStrikes };
    if (plan.mode === "single") return [opening];

    return [
        opening,
        { offset: plan.spanMinutes * 60_000, sounds: plan.closeStrikes },
    ];
}

/**
 * When each sound falls, in milliseconds from the press.
 *
 * The marks, with each opening and closing spread out at the cluster gap.
 * Sorted, because a three-sound opening on a one-minute span can reach into
 * the closing — the room's cooldown then drops whichever sound lands inside
 * another, which is also what would happen to a real pair of hands.
 */
export function gongOffsets(plan: GongPlan): number[] {
    const offsets: number[] = [];
    for (const marker of gongMarkers(plan)) {
        for (let sound = 0; sound < marker.sounds; sound += 1) {
            offsets.push(marker.offset + sound * GONG_CLUSTER_GAP_MS);
        }
    }
    return offsets.sort((left, right) => left - right);
}

/**
 * When the next strike lands, or `null` once the sequence is spent.
 *
 * Computed rather than stored, so the countdown on screen is derived from the
 * same plan that schedules the sound and the two cannot drift apart.
 */
export function nextGongAt(
    plan: GongPlan,
    startedAt: number,
    now: number
): number | null {
    for (const offset of gongOffsets(plan)) {
        const at = startedAt + offset;
        if (at > now) return at;
    }
    return null;
}

/**
 * The time left, for the corner of the screen.
 *
 * Rounded up, so the last minute reads 1:00 for the whole of it and 0:00
 * appears only as the gong actually lands — a countdown that shows 0:00 for a
 * second beforehand reads as a gong that failed.
 */
export function formatGongCountdown(remainingMs: number): string {
    const total = Math.max(0, Math.ceil(remainingMs / 1000));
    const seconds = total % 60;
    const minutes = Math.floor(total / 60) % 60;
    const hours = Math.floor(total / 3600);

    const pad = (value: number) => String(value).padStart(2, "0");
    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
}

/**
 * How long the room stays quiet after a gong.
 *
 * The sound itself rings for about five seconds. Two seconds and a half is
 * long enough that gongs never stack into a clang — a held-down key, a
 * double click, or a client that repeats itself — and short enough that a
 * therapist marking two moments in quick succession is not made to wait.
 */
export const GONG_MIN_INTERVAL_MS = 2500;

/**
 * Whether this participant may ring for everyone.
 *
 * The same rule, and the same reason, as clearing the whiteboard: the
 * identity is minted by the meeting service — `therapist-*` for an account
 * holder, `guest-*` for someone on a share link — and both sides apply it.
 * The sender decides whether to offer the control; the receiver decides
 * whether to obey the packet. Enforced only on the sending side, the rule
 * would be a hidden button, and anyone with a console could sound a gong in
 * their therapist's room.
 */
export function mayRingGong(identity: string): boolean {
    return identity.startsWith("therapist-");
}

/**
 * Whether enough quiet has passed to sound another one.
 *
 * Applied when a packet arrives as well as when the button is pressed, so a
 * client that ignores its own cooldown cannot stack gongs on anyone else.
 * A `now` earlier than the last gong means the clock moved under us, and
 * locking the gong out until it catches up would be worse than one extra
 * sound.
 */
export function gongIsDue(lastRungAt: number | null, now: number): boolean {
    if (lastRungAt === null) return true;
    if (now < lastRungAt) return true;
    return now - lastRungAt >= GONG_MIN_INTERVAL_MS;
}
